import { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles.css';
import { API_URL } from '../config';

function AddQuestion() {
  const [questionText, setQuestionText] = useState('');
  const [myQuestions, setMyQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    fetchMyQuestions();
  }, []);

  const fetchMyQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      // Get all questions and filter for user-created ones
      const response = await axios.get(`${API_URL}/api/admin/questions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Note: You may need to modify the backend to include created_by_user_id in the response
      // For now, we'll show all questions but you should filter by user
      setMyQuestions(response.data || []);
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError('Failed to load questions: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    
    if (!questionText.trim()) {
      setError('Question text cannot be empty');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/admin/questions`, 
        { text: questionText.trim() },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setMyQuestions([response.data, ...myQuestions]);
      setQuestionText('');
      showSuccess('Question added successfully!');
    } catch (err) {
      setError('Failed to add question: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question.id);
    setEditText(question.text || '');
  };

  const handleSaveEdit = async () => {
    if (!editText.trim()) {
      setError('Question text cannot be empty');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/api/admin/questions/${editingQuestion}`, 
        { text: editText.trim() },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Update local state
      setMyQuestions(myQuestions.map(question => 
        question.id === editingQuestion 
          ? { ...question, text: editText.trim() }
          : question
      ));
      
      setEditingQuestion(null);
      setEditText('');
      showSuccess('Question updated successfully!');
    } catch (err) {
      setError('Failed to update question: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question? This will also delete all answers to this question.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setMyQuestions(myQuestions.filter(question => question.id !== questionId));
      showSuccess('Question deleted successfully!');
    } catch (err) {
      setError('Failed to delete question: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setEditText('');
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  return (
    <div className="container">
      <nav className="dashboard-nav">
        <button onClick={() => window.location.href = '/dashboard'}>Dashboard</button>
        <button onClick={() => window.location.href = '/question-of-the-day'}>Question of the Day</button>
        <button onClick={() => window.location.href = '/ai-assistant'}>AI Assistant</button>
        <button onClick={() => {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }}>
          Logout
        </button>
      </nav>

      <h1>Add Custom Questions</h1>
      <p>Create your own questions to add to the rotation system. Your custom questions will be mixed in with the standard questions.</p>
      
      {error && <div className="error">{error}</div>}
      {successMessage && <div className="success">{successMessage}</div>}

      {/* Add New Question Form */}
      <div className="add-question-section" style={{ marginBottom: '40px' }}>
        <h2>Add New Question</h2>
        <form onSubmit={handleAddQuestion}>
          <textarea
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="Enter your custom question here... (e.g., 'What's your favorite way to spend a quiet evening together?')"
            rows="4"
            style={{ 
              width: '100%', 
              padding: '12px',
              marginBottom: '15px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px'
            }}
            maxLength={500}
            required
          />
          <div style={{ marginBottom: '15px' }}>
            <small style={{ color: '#666' }}>
              {questionText.length}/500 characters
            </small>
          </div>
          <button 
            type="submit" 
            disabled={isLoading || !questionText.trim()}
            className="btn-form"
          >
            {isLoading ? 'Adding...' : 'Add Question'}
          </button>
        </form>
      </div>

      {/* My Questions List */}
      <div className="my-questions-section">
        <h2>My Custom Questions ({myQuestions.length})</h2>
        
        {myQuestions.length > 0 ? (
          <div className="questions-list">
            {myQuestions.map((question) => (
              <div key={question.id} className="question-card" style={{
                border: '1px solid #ddd',
                padding: '20px',
                marginBottom: '15px',
                borderRadius: '8px',
                backgroundColor: '#f9f9f9'
              }}>
                {editingQuestion === question.id ? (
                  <div className="edit-section">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows="3"
                      style={{ 
                        width: '100%', 
                        padding: '10px',
                        marginBottom: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                      maxLength={500}
                    />
                    <div style={{ marginBottom: '10px' }}>
                      <small style={{ color: '#666' }}>
                        {editText.length}/500 characters
                      </small>
                    </div>
                    <div className="edit-buttons">
                      <button 
                        onClick={handleSaveEdit}
                        style={{ marginRight: '10px' }}
                      >
                        Save
                      </button>
                      <button onClick={handleCancelEdit}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p style={{ marginBottom: '15px', lineHeight: '1.5' }}>
                      {question.text}
                    </p>
                    <div className="question-actions">
                      <button 
                        onClick={() => handleEditQuestion(question)}
                        style={{ marginRight: '10px' }}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteQuestion(question.id)}
                        className="danger-button"
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-questions">
            <p>You haven't created any custom questions yet. Add your first question above!</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddQuestion;